/**
 * Demo Mode Setup
 *
 * Call setupDemoMode() before the app renders to replace all API modules
 * with demo implementations that return local data from PostgreSQL export.
 */

import { authApi } from '../auth';
import { userApi } from '../user';
import { agentApi } from '../agents';
import { feedApi } from '../feed';
import { timerApi } from '../timer';
import { memorialApi } from '../memorial';
import { skillApi } from '../skills';
import { skillShopApi } from '../skillShop';
import { experienceApi } from '../experiences';
import { mediaApi } from '../media';
import { settingsApi } from '../settings';
import { conversationApi } from '../conversations';
import { legacyApi } from '../legacy';
import { taskApi } from '../tasks';
import { channelApi } from '../channels';
import { notificationApi } from '../notifications';

import {
  demoAuthApi,
  demoUserApi,
  demoAgentApi,
  demoFeedApi,
  demoConversationApi,
  demoTimerApi,
  demoMemorialApi,
  demoSkillApi,
  demoSkillShopApi,
  demoLegacyApi,
  demoExperienceApi,
  demoMediaApi,
  demoSettingsApi,
  demoNotificationApi,
  demoChannelApi,
  demoTaskApi,
} from './demoApi';
import { tokenStorage, userStorage } from '../../utils/storage';
import { demoUser } from './demoData';

function patchObject(target: Record<string, unknown>, source: Record<string, unknown>) {
  for (const key of Object.keys(target)) {
    if (key in source) {
      target[key] = source[key];
    }
  }
}

export function setupDemoMode() {
  console.log(
    '%c🎭 ALIVE Demo Mode Active%c — all data served locally, no backend required.',
    'color: #10b981; font-weight: bold; font-size: 14px;',
    'color: #6b7280; font-size: 12px;',
  );

  // Pre-seed auth so checkAuth() finds a valid token and skips the real HTTP call
  tokenStorage.set('demo-token-static');
  userStorage.set(demoUser);

  // Mark onboarding as completed for demo
  try {
    const authStorage = JSON.parse(localStorage.getItem('auth-storage') || '{}');
    authStorage.state = { ...authStorage.state, hasCompletedOnboarding: true };
    localStorage.setItem('auth-storage', JSON.stringify(authStorage));
  } catch { /* ignore */ }

  // Patch each API object in-place so all existing imports pick up the demo impl
  patchObject(authApi as unknown as Record<string, unknown>, demoAuthApi as unknown as Record<string, unknown>);
  patchObject(userApi as unknown as Record<string, unknown>, demoUserApi as unknown as Record<string, unknown>);
  patchObject(agentApi as unknown as Record<string, unknown>, demoAgentApi as unknown as Record<string, unknown>);
  patchObject(feedApi as unknown as Record<string, unknown>, demoFeedApi as unknown as Record<string, unknown>);
  patchObject(conversationApi as unknown as Record<string, unknown>, demoConversationApi as unknown as Record<string, unknown>);
  patchObject(timerApi as unknown as Record<string, unknown>, demoTimerApi as unknown as Record<string, unknown>);
  patchObject(memorialApi as unknown as Record<string, unknown>, demoMemorialApi as unknown as Record<string, unknown>);
  patchObject(skillApi as unknown as Record<string, unknown>, demoSkillApi as unknown as Record<string, unknown>);
  patchObject(skillShopApi as unknown as Record<string, unknown>, demoSkillShopApi as unknown as Record<string, unknown>);
  patchObject(legacyApi as unknown as Record<string, unknown>, demoLegacyApi as unknown as Record<string, unknown>);
  patchObject(experienceApi as unknown as Record<string, unknown>, demoExperienceApi as unknown as Record<string, unknown>);
  patchObject(mediaApi as unknown as Record<string, unknown>, demoMediaApi as unknown as Record<string, unknown>);
  patchObject(settingsApi as unknown as Record<string, unknown>, demoSettingsApi as unknown as Record<string, unknown>);
  patchObject(notificationApi as unknown as Record<string, unknown>, demoNotificationApi as unknown as Record<string, unknown>);
  patchObject(channelApi as unknown as Record<string, unknown>, demoChannelApi as unknown as Record<string, unknown>);
  patchObject(taskApi as unknown as Record<string, unknown>, demoTaskApi as unknown as Record<string, unknown>);
}

export function isDemoMode(): boolean {
  return import.meta.env.VITE_DEMO_MODE === 'true';
}
